/**
 * Global store for player data overrides (wage, est valuation).
 *
 * A player's optimistic override is scoped to whichever view (Current Squad
 * or one scenario) was active when it was set — `scenarioStore`'s active-
 * scenario switch calls {@link clearPlayerOverrides} on every transition
 * (create, delete, and explicit `setActiveScenario`), so a value confirmed in
 * one view never bleeds into another. SE-8444: before this, the map was keyed
 * on `playerId` alone and genuinely persisted across scenario switches, so a
 * manual-salary edit in one scenario visually leaked into every other
 * scenario and Current Squad until a full reload — the underlying server
 * data was always correctly isolated per scenario (see
 * `scenario.service.ts`'s `copyManualSalariesIntoScenario`); only this
 * client-side optimism was unscoped. The map still only needs `playerId` as
 * its key: clearing on every switch is what makes that safe, since whichever
 * view you land on next re-fetches its own (already-isolated) server data —
 * the override's only job is to bridge the gap before that fetch resolves.
 * Uses Apollo reactive variable for cross-component reactivity.
 * @module features/squad-planning/stores/playerOverridesStore
 */

import { makeVar, useReactiveVar } from "@apollo/client";
import { useCallback } from "react";

/** Override data for a single player */
export interface PlayerOverride {
  /** Overridden gross annual salary, or undefined if not overridden */
  readonly wage?: number;
  /** Overridden estimated valuation, or undefined if not overridden */
  readonly estValuation?: number;
}

/** Map of player ID to overrides */
type PlayerOverridesMap = Readonly<Record<string, PlayerOverride>>;

/** Reactive variable holding all player overrides */
const playerOverridesVar = makeVar<PlayerOverridesMap>({});

/**
 * SE-8444 — clears every player's optimistic override. Called by
 * `scenarioStore` on every active-scenario transition (Current Squad ↔ any
 * scenario, and scenario ↔ scenario) so a value confirmed in one view cannot
 * visually leak into the next.
 */
export function clearPlayerOverrides(): void {
  playerOverridesVar({});
}

/**
 * Hook for reading and updating player data overrides.
 * @returns Override map and setter functions
 */
export function usePlayerOverrides() {
  const overrides = useReactiveVar(playerOverridesVar);

  const setWage = useCallback((playerId: string, wage: number | null) => {
    const current = playerOverridesVar();
    const existing = current[playerId] ?? {};
    playerOverridesVar({
      ...current,
      [playerId]:
        wage != null ? { ...existing, wage } : { ...existing, wage: undefined },
    });
  }, []);

  const setEstValuation = useCallback(
    (playerId: string, estValuation: number | null) => {
      const current = playerOverridesVar();
      const existing = current[playerId] ?? {};
      playerOverridesVar({
        ...current,
        [playerId]:
          estValuation != null
            ? { ...existing, estValuation }
            : { ...existing, estValuation: undefined },
      });
    },
    []
  );

  return { overrides, setWage, setEstValuation };
}
